import { useState } from 'react';
import { DatePicker, Modal } from 'antd';
import { ISeaweedLineResource } from '../../../entities/farms.entities';
import TimeTrackInput, {
  toSecTimeTracks,
} from '../../shared/input/TimeTrackInput';
import { Button, CloseIcon, Input, Subtitle } from '../../shared';
import { useDispatch, useSelector } from 'react-redux';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import { sendSingleRequest } from '../../../apis';
import { showFeedback } from '../../../store/ui/ui.actions';
import { ICustomValue } from '../../../entities/automation.entities';
import { loadFarmsData } from '../../../store/farms/farms.actions';
import { IFileWall, ITimeRange } from '../../../entities/general.entities';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate } from '../../../lib/lang.helper';
import MinMaxInputForm from '../SeedingModal/MinMaxInputForm';
import {
  numToStr,
  toSecond,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import { selectAccount } from '../../../store/auth/auth.selector';
const { RangePicker } = DatePicker;

interface IFormData {
  tank_area: number | null;
  spat_size: number;
  spat_size_max: number | null;
  comment: string | null;
  image_pdf_files?: IFileWall[];
  time_tracks?: ITimeRange[];
  custom_values?: ICustomValue[];
}

const defaultForm: IFormData = {
  tank_area: null,
  spat_size: 0,
  spat_size_max: null,
  comment: null,
  image_pdf_files: [],
  time_tracks: [],
};

interface Props {
  visible: boolean;
  title: string;
  lineData: ISeaweedLineResource;
  onCancel: () => void;
  onConfirm: (data: any) => void;
}

const SeaweedCatchSpatModal = ({
  visible,
  title,
  lineData,
  onCancel,
  onConfirm,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;

  const [disabled, setDisabled] = useState(false);
  const [startDate, setStartDate] = useState(0);
  const [endDate, setEndDate] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fieldData, setFieldData] = useState<IFormData>(defaultForm);

  const fieldValid = () => {
    if (startDate === 0 || endDate === 0) {
      setError(translate(lang, 'Please select range'));
      return null;
    }
    if (
      fieldData.spat_size_max !== null &&
      fieldData.spat_size > fieldData.spat_size_max
    ) {
      setError(translate(lang, '_invalid_spat_size_range'));
      return null;
    }
    if (!fieldData.tank_area || fieldData.tank_area <= 0) {
      setError(translate(lang, 'Invalid tank area'));
      return null;
    }
    let res = {
      planned_date_seed: toSecond(startDate),
      planned_date_harvest: toSecond(endDate),
      line_id: lineData.id,
      ...fieldData,
    };
    if (res.time_tracks && res.time_tracks.length > 0) {
      const tt = toSecTimeTracks(res.time_tracks);
      if (!tt) {
        setError(translate(lang, '_invalid_time_tracks_info'));
        return null;
      }
      res.time_tracks = tt;
    }
    return res;
  };
  const onRangeChange = (dates: any, dateStrings: any) => {
    setStartDate(dates[0].toDate().getTime());
    setEndDate(dates[1].toDate().getTime());
    setError(null);
  };
  const updateField = (key: keyof IFormData, val: any) => {
    setFieldData({ ...fieldData, [key]: val });
  };
  const handleConfirmClick = async () => {
    const form = fieldValid();
    if (!form) return;

    setDisabled(true);
    const res = await sendSingleRequest(
      form,
      'POST',
      'api/farm/line/catch-spat',
      true,
    );
    if (res.status) {
      dispatch(loadFarmsData());
      onConfirm(res.data);
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, res.data?.message ?? 'Success'),
        }),
      );
    } else {
      setError(translate(lang, res.data?.message ?? 'Request failed'));
      setDisabled(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={650}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {title}
          </Subtitle>
        </div>
        <div>
          <label>{translate(lang, '_catch_spat_range')}</label>
          <div className='mt-16 mb-32'>
            <RangePicker onChange={onRangeChange} />
          </div>
          {error && (
            <div
              className='invalid-form'
              style={{ fontSize: '15px', textAlign: 'center' }}
            >
              <div className='invalid-feedback'>{error}</div>
            </div>
          )}
          {fieldData.time_tracks && (
            <TimeTrackInput
              data={fieldData.time_tracks}
              onChange={t => updateField('time_tracks', t)}
              baseDate={startDate}
            />
          )}
          <MinMaxInputForm
            label={translate(lang, 'Spat Size')}
            min={fieldData.spat_size}
            max={fieldData.spat_size_max}
            unit={getUnitOfSpecie(unitType, 'size_mm')}
            onMinChange={v => updateField('spat_size', v)}
            onMaxChange={v => updateField('spat_size_max', v)}
            disabled={disabled}
          />
          <div className='mb-17'>
            <Input
              label={translate(lang, 'Tank Area')}
              type='number'
              value={numToStr(fieldData.tank_area)}
              required
              onChange={e => updateField('tank_area', Number(e.target.value))}
              unit={getUnitOfSpecie(unitType, 'area_sqm')}
            />
          </div>
          <div className='mt-17 mb-17'>
            <Input
              label={translate(lang, 'Comment')}
              placeholder={translate(lang, 'Comment')}
              type='textarea'
              value={fieldData.comment ?? ''}
              onChange={e => updateField('comment', e.target.value)}
            />
          </div>
          <div className='mb-17'>
            <FilesWall
              label={translate(lang, 'Images & PDF Files')}
              fileList={fieldData.image_pdf_files}
              onChange={v => updateField('image_pdf_files', v)}
            />
          </div>
        </div>
      </div>
      <div className='modal-button d-flex justify-end align-center'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={handleConfirmClick}
          disabled={disabled}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default SeaweedCatchSpatModal;
